import { useCallback, useMemo, useState } from 'react'

import { zodResolver } from '@hookform/resolvers/zod'
import { useUnit } from 'effector-react'
import { FormProvider, useForm, useWatch } from 'react-hook-form'
import { useNavigate } from 'react-router'
import { z } from 'zod'

import ClockIcon from '@/assets/icons/icon-clock.svg'
import InfoIcon from '@/assets/icons/icon-info.svg'
import PlusIcon from '@/assets/icons/icon-plus.svg'
import { Page } from '@/components/layout/page/page.tsx'
import { Button } from '@/components/ui/button.tsx'
import { Checkbox } from '@/components/ui/checkbox.tsx'
import { EmptyProvider } from '@/components/ui/empty/empty-provider.tsx'
import { Separator } from '@/components/ui/separator.tsx'
import { Tooltip } from '@/components/ui/tooltip.tsx'
import { DIALOG_TYPE } from '@/dialogs/constants.ts'
import { $creatorId, $currentCreator } from '@/models/creators.ts'
import { openDialog } from '@/models/dialogs.ts'
import {
  compareTimes,
  displayTime,
  getDateWithHoursAndMinutesFromTimeObject,
  getHoursAndMinutes,
} from '@/modules/creator-settings/rebill-messages/helpers/time.ts'
import { useCreateRebillMessageMutation } from '@/modules/creator-settings/rebill-messages/hooks/use-create-rebill-message.ts'
import { useUpdateRebillMessageMutation } from '@/modules/creator-settings/rebill-messages/hooks/use-update-rebill-message.ts'
import type { RebillMessage } from '@/modules/creator-settings/rebill-messages/types.ts'
import { useSettingsContext } from '@/modules/creator-settings/state/SettingsProvider.tsx'
import { SettingsOption } from '@/modules/creator-settings/ui/SettingsOption.tsx'
import { DefaultSchemaBase } from '@/modules/message/components/editor/schema'
import { MessageSequenceEditor } from '@/modules/message/components/sequence/message-sequence-editor'
import { MessageContextType } from '@/modules/message/components/sequence/types.ts'
import type { Message } from '@/modules/online-mass-messages/types.ts'
import { EmptyMessages } from '@/modules/online-mass-messages/ui/error/empty-messages'
import { ListsSelector } from '@/modules/online-mass-messages/ui/list-selector.tsx'
import { transformListsToSelectObject } from '@/modules/online-mass-messages/utils.ts'
import { AppRoutes } from '@/utils/constants.ts'

const dialogTitle = 'Add a Rebill Message'
const dialogDescription = (
  <>
    Use the button to insert the fan&#39;s name, which will replace the label
    with their actual name. If the fan doesn&#39;t have a name, the label will
    be replaced with the fallback name.
  </>
)

const editorComponents = [
  'text',
  'media',
  'price',
  'fallback',
  'customAndFanNameLabels',
] as const

const Schema = DefaultSchemaBase.extend({ sequenceId: z.number().optional() })
const resolver = zodResolver(Schema)

export type MessageFieldType = Message & { id: number }

interface Props {
  data?: RebillMessage
  isEdit?: boolean
}

export const ActionsRebillMessagesPage = ({ data, isEdit }: Props) => {
  const redirect = useNavigate()
  const creatorId = useUnit($creatorId)
  const activeCreator = useUnit($currentCreator)
  const [messages, setMessages] = useState<MessageFieldType[]>(
    (data?.messages as MessageFieldType[]) || [],
  )
  const [times, setTimes] = useState({
    fromTime: getDateWithHoursAndMinutesFromTimeObject(data?.timeframeStart),
    toTime: getDateWithHoursAndMinutesFromTimeObject(data?.timeframeEnd),
  })
  const { rebillMessages } = useSettingsContext()
  const [withoutTimeframe, setWithoutTimeframe] = useState(
    isEdit && !data?.timeframeStart && !data?.timeframeEnd,
  )

  const form = useForm({
    defaultValues: {
      excludeList: data?.excludedFanLists || [],
      includeList: data?.includedFanLists || [],
    },
  })

  const [excludedFanLists, includedFanLists] = useWatch({
    name: ['excludeList', 'includeList'],
    control: form.control,
  })

  const createRebillMessageMutation = useCreateRebillMessageMutation()
  const updateRebillMessageMutation = useUpdateRebillMessageMutation()
  const handleRedirect = () => {
    if (!creatorId) {
      return
    }
    redirect(AppRoutes.Creator.Settings.RebillMessages.asUrl(creatorId))
  }

  const openTimeSelectDialog = () => {
    openDialog({
      type: DIALOG_TYPE.SELECT_TIME_POPUP,
      title: 'Timeframe',
      fromTime: times.fromTime,
      toTime: times.toTime,
      id: data?.id,
      update: setTimes,
      bookedIntervals: bookedIntervals,
    })
  }

  const createMessage = () => {
    const values = form.getValues()
    const submitData: RebillMessage = {
      id: data?.id,
      messages: messages,
      timeframeStart: withoutTimeframe
        ? null
        : getHoursAndMinutes(times.fromTime),
      timeframeEnd: withoutTimeframe ? null : getHoursAndMinutes(times.toTime),
      includedFanLists: values.includeList,
      excludedFanLists: values.excludeList,
    }

    if (isEdit) {
      updateRebillMessageMutation.mutate(submitData, {
        onSuccess: () => {
          handleRedirect()
        },
      })

      return
    }

    createRebillMessageMutation.mutate(submitData, {
      onSuccess: () => {
        handleRedirect()
      },
    })
  }

  const handleSaveLocalMessage = useCallback(
    async (message: MessageFieldType) => {
      const data = {
        ...message,
        id: messages.length,
      }

      setMessages([...messages, data])
    },
    [messages],
  )

  const handleUpdateLocalMessage = useCallback(
    async (message: MessageFieldType) => {
      const newMessages = messages.map((m) => {
        return m.id === message.id ? message : m
      })

      setMessages(newMessages)
    },
    [messages],
  )

  const handleRemoveMessage = useCallback(
    async ({ id }: { id: number }) => {
      const copyMessages = messages.filter((m) => {
        return m.id !== id
      })

      setMessages(copyMessages)
    },
    [messages],
  )

  const handleWithoutTimeframe = () => {
    setWithoutTimeframe(!withoutTimeframe)
  }

  const isDefaultMessageExist = useMemo(() => {
    const defaultMessages = rebillMessages.find(
      (m) => !m.timeframeStart && !m.timeframeEnd,
    )

    if (!defaultMessages) {
      return false
    }

    return defaultMessages && defaultMessages.id !== data?.id
  }, [data?.id, rebillMessages])

  const bookedIntervals = useMemo(() => {
    return rebillMessages
      .filter((m) => m.id !== data?.id && m.timeframeStart && m.timeframeEnd)
      .sort((a, b) => compareTimes(a.timeframeStart!, b.timeframeStart!))
      .map((message) => ({
        start: message.timeframeStart!,
        end: message.timeframeEnd!,
      }))
  }, [data?.id, rebillMessages])

  const listOptions = transformListsToSelectObject(activeCreator?.lists)

  return (
    <Page
      title="Create Rebill Messages"
      description="Configure your Rebill Messages to be sent out to fans who renew their subscription."
      backToPage={{
        name: 'Rebill Messages',
        url: AppRoutes.Creator.Settings.RebillMessages.toString(),
      }}
    >
      <div>
        <Separator />

        <div className="text-title flex items-center gap-2 py-8 font-medium">
          <Checkbox
            checked={withoutTimeframe}
            onClick={handleWithoutTimeframe}
            disabled={isDefaultMessageExist}
          />
          Create a message without timeframe{' '}
          <Tooltip
            side="right"
            sideOffset={20}
            content={
              isDefaultMessageExist
                ? 'Default message already exist'
                : 'If message for this time not exist this message will be as default'
            }
          >
            <InfoIcon className="text-secondary-300 h-4 w-4" />
          </Tooltip>
        </div>

        {!withoutTimeframe && (
          <>
            <Separator />
            <SettingsOption
              title="Timeframe"
              description="The timeframe in which this message should be sent out to a fan."
              mobileVertical
              biggerTitle
            >
              <div
                className="border-secondary-300 bg-card text-secondary-300 hover:border-secondary-100 flex cursor-pointer select-none items-center gap-2 rounded-md border-2 px-3 py-2"
                onClick={openTimeSelectDialog}
              >
                <ClockIcon />
                <span>{displayTime(times.fromTime!)}</span>
                {'- '}
                {displayTime(times.toTime!)}
              </div>
            </SettingsOption>
          </>
        )}

        <Separator />

        <div className="flex flex-wrap justify-between">
          <SettingsOption
            title="Fan Lists Control"
            description="Include or exclude fans from specific lists for this Rebill Messages"
            mobileVertical
            biggerTitle
          />
          <div className="flex flex-col gap-10 py-6 md:flex-row">
            <FormProvider {...form}>
              <ListsSelector
                label="Only include the selected Fan Lists"
                name="includeList"
                placeholder="Select Lists"
                lists={listOptions}
                backgroundCard
                excludeValues={excludedFanLists}
              />

              <ListsSelector
                label="Exclude the selected Fan Lists"
                placeholder="Select Lists"
                name="excludeList"
                lists={listOptions}
                backgroundCard
                excludeValues={includedFanLists}
              />
            </FormProvider>
          </div>
        </div>

        <Separator />

        <EmptyProvider render={() => <EmptyMessages action="Add Message" />}>
          <MessageSequenceEditor
            title="Messages"
            description="Configure multiple messages that will be sent to your fans"
            placeholder="Hey %name%, how are you?"
            addMessageLabel="Add Message"
            dialogTitle={dialogTitle}
            dialogDescription={dialogDescription}
            components={editorComponents}
            schema={resolver}
            entries={messages}
            onCreate={handleSaveLocalMessage}
            onUpdate={handleUpdateLocalMessage}
            onRemove={handleRemoveMessage}
            type={MessageContextType.FollowUpMessage}
          />
        </EmptyProvider>

        <div className="mt-8 flex justify-end">
          <Button
            disabled={!messages.length}
            onClick={createMessage}
            isLoading={
              updateRebillMessageMutation.isLoading ||
              createRebillMessageMutation.isLoading
            }
          >
            {isEdit ? (
              'Update'
            ) : (
              <>
                <PlusIcon /> Create{' '}
              </>
            )}{' '}
            Rebill Message
          </Button>
        </div>
      </div>
    </Page>
  )
}
